import React, { useEffect, useState } from 'react'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    makeStyles,
    TextField,
} from '@material-ui/core'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import { useTranslation } from 'react-i18next'
import { createTenantUser, renameTenantUser, setTenantUserPassword, TenantUserInfo } from '../../redux/actions/dashboard'
import { generatePassword, validateTenantPass } from './SetPasswordDialog'

const isValidLogin = (login: string) => Boolean(login.trim()) && !/\s/.test(login.trim())

const useStyles = makeStyles((theme) => ({
    field: {
        marginTop: theme.spacing(1),
        width: 400,
        display: 'block',
    },
}))

interface Props {
    open: boolean
    user?: TenantUserInfo
    tenantId?: string
    tenantName?: string
    onClose: () => void
    onSaved: () => void
    onError: (msg: string) => void
}

export default function TenantUserDialog({ open, user, tenantId, tenantName, onClose, onSaved, onError }: Props) {
    const classes = useStyles()
    const { t } = useTranslation()
    const [login, setLogin] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [touched, setTouched] = useState(false)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!open) return
        setLogin(user?.login ?? '')
        setPassword('')
        setShowPassword(false)
        setTouched(false)
    }, [open, user])

    const isNew = !user
    const loginChanged = isNew || login.trim() !== user?.login
    const loginError = touched && !isValidLogin(login)
    const passwordError = password.length > 0 && !validateTenantPass(password)
    const passwordOk = isNew ? validateTenantPass(password) : !password || validateTenantPass(password)
    const hasChanges = isNew || loginChanged || password.length > 0
    const canSave = hasChanges && isValidLogin(login) && passwordOk && !saving

    const close = () => {
        if (!saving) onClose()
    }

    const handleSave = async () => {
        setTouched(true)
        if (!canSave) return
        setSaving(true)
        try {
            if (isNew) {
                await createTenantUser(login.trim(), password, tenantId)
            } else {
                if (loginChanged) await renameTenantUser(user!.id, login.trim())
                if (password) await setTenantUserPassword(user!.id, password)
            }
            onSaved()
        } catch {
            onError(t('dashboard.errorSavingLogin'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} onClose={close}>
            <DialogTitle>
                {isNew ? t('dashboard.addAdmin') : t('dashboard.editAdmin')}
                {tenantName ? ` – ${tenantName}` : ''}
            </DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    required
                    fullWidth
                    className={classes.field}
                    variant="filled"
                    label={t('dashboard.login')}
                    value={login}
                    inputProps={{ maxLength: 256 }}
                    error={loginError}
                    FormHelperTextProps={{ style: { marginLeft: 0, marginRight: 0 } }}
                    helperText={loginError ? t('dashboard.invalidLogin') : ' '}
                    onChange={(e) => setLogin(e.target.value)}
                />
                <TextField
                    required={isNew}
                    fullWidth
                    className={classes.field}
                    variant="filled"
                    label={t('common.newPassword')}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    error={passwordError}
                    FormHelperTextProps={{ style: { marginLeft: 0, marginRight: 0 } }}
                    helperText={
                        passwordError
                            ? t('dashboard.passwordRules')
                            : isNew
                            ? t('dashboard.passwordRules')
                            : t('dashboard.newPasswordOptional')
                    }
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        setPassword(generatePassword())
                        setShowPassword(true)
                    }}
                    disabled={saving}
                >
                    {t('dashboard.generatePassword')}
                </Button>
                <div style={{ flex: 1 }} />
                <Button onClick={close} disabled={saving}>
                    {t('common.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleSave} disabled={!canSave}>
                    {saving ? <CircularProgress size={20} /> : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
